/** @jsxRuntime classic */
/** @jsx jsx */
import { theme } from '@circleci/compass/dist/theme';
import { jsx, SerializedStyles } from '@emotion/core';
import { injectIntl, IntlShape, MessageDescriptor } from 'react-intl';

import { outer } from '~/globalStyles';
import { useAnalytics, Action } from '~/hooks/useAnalytics';
import { styled } from '~/styled';

// jsx has to be imported for @emotion/core css to work but
// it is never explicitly used. This tricks eslint and typescript
// into thinking it is used
// oxlint-disable-next-line no-unused-expressions
jsx;

interface IntlExternalLinkProps {
  intl: IntlShape;
  className?: string;
  localizationKey?: MessageDescriptor;
  href?: string;
  children: React.ReactNode;
  trackActionEvent?: string;
  trackActionText?: string;
  customStyles?: SerializedStyles;
  withExternalWindowIcon?: boolean;
}
const ExternalLinkWrapper = styled.div`
  display: inline;
  align-items: center;
  line-height: ${theme.space.space24};
`;

interface SyledAProps {
  withExternalWindowIcon?: boolean;
}

// as certain IntlExternalLink use cases (mainly for links in footer) adhere to a certain style, the added prop is to make the component more dynamic and provide more use cases to allow for external links in devhub to behave consistently
const A = styled.a<SyledAProps>(() => {
  return `
  text-decoration: none;
  color: ${outer.colors.blueDark};
  font-family: ${theme.fonts.sansSerif};
  font-size: ${theme.fontSizes.fontsize16};
  font-weight: 500;
  letter-spacing: 0.19px;
  line-height: ${theme.fontSizes.fontsize16};

  :hover {
    -webkit-text-stroke: ${outer.textStroke};
  }
`;
});

const IconWrapper = styled.span`
  margin-left: ${theme.space.space4};
  display: inline-flex;
  position: relative;
  top: 2px;
  > svg {
    width: 16px;
    min-width: 16px;
    height: 16px;
    color: #207fc6;
  }
`;

/*
 * Importing the SVG icon directly instead of '@circleci/icons' because it
 * breaks layout and hot-reload when used in internationalization.
 * We suspect it might be a bug from <FormattedMessage /> when the
 * icon is used as a value (check <OrbPage /> for an use-case of that).
 */
const NewWindowSVG = () => {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className="css-bleycz"
    >
      <path
        fillRule="evenodd"
        d="M17,19 L5,19 L5,7 L10,7 L10,5.00083798 L4.44085883,5.00083798 C4.05495161,4.9876138 3.68075435,5.13139282 3.4077085,5.39780868 C3.13466265,5.66422454 2.98730555,6.02933568 3.00085883,6.40587245 L3.00085883,19.5941275 C2.98730555,19.9706643 3.13466265,20.3357755 3.4077085,20.6021913 C3.68075435,20.8686072 4.05495161,21.0123862 4.44085883,20.999162 L17.56,20.999162 C18.35529,20.999162 19,20.3701067 19,19.5941275 L19,14 L17,14 L17,19 Z M17.5857864,5 L14,5 C13.4477153,5 13,4.55228475 13,4 C13,3.44771525 13.4477153,3 14,3 L20,3 C20.5522847,3 21,3.44771525 21,4 L21,10 C21,10.5522847 20.5522847,11 20,11 C19.4477153,11 19,10.5522847 19,10 L19,6.41421356 L14.8786797,10.5355339 C14.4881554,10.9260582 13.8549904,10.9260582 13.4644661,10.5355339 C13.0739418,10.1450096 13.0739418,9.51184464 13.4644661,9.12132034 L17.5857864,5 Z"
      ></path>
    </svg>
  );
};

const IntlExternalLink = ({
  intl,
  className,
  localizationKey,
  href,
  children,
  customStyles,
  withExternalWindowIcon,
  trackActionEvent,
  trackActionText,
  ...otherProps
}: IntlExternalLinkProps & React.HTMLProps<HTMLAnchorElement>) => {
  const { trackStandardizedAction } = useAnalytics();
  const handleClick = (event, type) => {
    trackStandardizedAction(event, Action.clicked, { type });
  };

  let hrefValue = href;
  if (localizationKey) {
    hrefValue = intl.formatMessage(localizationKey);
  }
  return (
    <ExternalLinkWrapper>
      <A
        onClick={() => {
          return handleClick(trackActionEvent, trackActionText);
        }}
        target="_blank"
        rel="noopener noreferrer"
        css={customStyles}
        className={className}
        href={hrefValue}
        {...otherProps}
        withExternalWindowIcon={withExternalWindowIcon}
      >
        {children}
      </A>
      {withExternalWindowIcon ? (
        <IconWrapper>
          <NewWindowSVG />
        </IconWrapper>
      ) : null}
    </ExternalLinkWrapper>
  );
};

export default injectIntl(IntlExternalLink);
